import { assistantTool } from "./assistant/index.js";
import { geoaiTool } from "./geoai/index.js";
import { linePlanningTool } from "./line-planning/index.js";
import { mbswathPlannerTool } from "./mbswath-planner/index.js";
import { lineworkTool } from "./linework/index.js";
import { overlayManagerTool } from "./overlay-manager/index.js";
import { globalLayersTool } from "./global-layers/index.js";
import { projectionManagerTool } from "./projection-manager/index.js";
import { etopoSoundingTool } from "./etopo-sounding/index.js";
import { crossSectionTool } from "./cross-section/index.js";
import { contoursTool } from "./contours/index.js";
import { changeDetectionTool } from "./change-detection/index.js";
import { volumeCalculationTool } from "./volume-calculation/index.js";
import { satelliteDerivedBathymetryTool } from "./satellite-derived-bathymetry/index.js";
import { imageGeneratorTool, marinaPortfolioManagerTool, digitalAssetManagerTool, projectManagementTool, ganttTool } from "./image-generator/index.js";
import { slipDeliveryTool, slipInventoryTableTool } from "./slip-delivery/index.js";
import { dockBuilderTool } from "./dock-builder/index.js";
import { slipValidationTool } from "./slip-validation/index.js";
import { realtimeWeatherTool } from "./realtime-weather/index.js";
import { historicalWeatherTool } from "./historical-weather/index.js";
import { tidesTool } from "./tides/index.js";
import { bagS102ConverterTool } from "./bag-s102-converter/index.js";
import { qimeraCommandTool } from "./qimera-command/index.js";
import { fmgtControllerTool } from "./fmgt-controller/index.js";
import { pointCloudWorkspaceTool } from "./point-cloud-workspace/index.js";
import { potreeBulkGeneratorTool } from "./potree-bulk-generator/index.js";
import { depthExtractionTool } from "./depth-extraction/index.js";
import { patchTestTool } from "./patch-test/index.js";
import { surfacesTool } from "./surfaces/index.js";
import { slipDepthExtractionTool } from "./slip-depth-extraction/index.js";
import { hydrostackConsoleTool } from "./hydrostack-console/index.js";
import { hydrostackSwathTool } from "./hydrostack-swath/index.js";
import { ehydroTool } from "./ehydro/index.js";
import { remoteDataTool } from "./remote-data/index.js";
import { noaaLidarTool } from "./noaa-lidar/index.js";
import { fieldCheckTool } from "./field-check/index.js";
import { encChartsTool } from "./enc-charts/index.js";
import { travelPlanningTool } from "./travel-planning/index.js";
import { waterClarityTool } from "./water-clarity/index.js";
import { faaAirTrafficTool, aisVesselTrafficTool } from "./live-traffic/index.js";
import { coastPilotTool } from "./coast-pilot/index.js";
import { coastalWebcamsTool } from "./coastal-webcams/index.js";
import { streetView360Tool } from "./streetview-360/index.js";
import { photoMosaicTool } from "./photo-mosaic/index.js";
import { sonarMosaicEditorTool } from "./sonar-mosaic-editor/index.js";
import { opusGnssTool } from "./opus-gnss/index.js";
import { systemMapTool } from "./system-map/index.js";
import {
  forestFiresTool,
  floodPlanModelTool,
  stormSurgeModelTool,
  openDriftTool,
  illegalFishingReportsTool,
  fieldLogTool,
  vesselLogTool,
  marineMammalLogTool,
  dataTransferTool,
  localNoticeTool,
  fisheriesInteractionTool,
  s57s101Tool,
} from "./maritime-ops/index.js";
import { dryStorage3dTool } from "./dry-storage-3d/index.js";
import { datumCalculatorTool } from "./datum-calculator/index.js";
import { tidalZoningTool } from "./tidal-zoning/index.js";
import { gaugeDatumWizardTool } from "./gauge-datum-wizard/index.js";

const ENABLED_KEY = "hydrotools:enabled-tools:v2";
const DOCK_BUILDER_SPLIT_KEY = "hydrotools:dock-builder-split:v1";
const ORDER_KEY = "hydrotools:tool-order:v2";
const CATEGORY_ORDER_KEY = "hydrotools:category-order:v1";
const DOCK_KEY = "hydrotools:tool-docks:v2";
const BUILDER_CATEGORY_OPEN_KEY = "hydrotools:builder-category-open:v2";
const DOCK_CATEGORY_OPEN_KEY = "hydrotools:dock-category-open:v2";
const OPEN_PANELS_KEY = "hydrotools:open-panels:v2";
const DEFAULT_ENABLED_TOOL_IDS = ["global-layers", "overlay-manager", "hydrostack-console", "hydrostack-swath", "image-generator", "slip-delivery", "dock-builder", "streetview-360", "photo-mosaic", "opus-gnss", "point-cloud-workspace", "realtime-weather", "historical-weather", "tides", "datum-calculator", "tidal-zoning", "gauge-datum-wizard", "travel-planning", "coast-pilot-explorer", "coastal-webcams", "faa-air-traffic", "ais-vessel-traffic", "line-planning", "mbswath-planner", "linework", "projection-manager", "etopo-sounding", "satellite-derived-bathymetry", "cross-section", "contours", "change-detection", "volume-calculation", "patch-test"];
const LEFT_DOCK_TOOL_IDS = new Set(["global-layers"]);
const BOTTOM_OWNER_TOOL_IDS = new Set([]);
const DEFAULT_BOTTOM_DOCK_TOOL_IDS = new Set([]);
const DEFAULT_TOOL_STATE_VERSION = 1;
// Alphabetical -- no category (including client toolsets like "Safe Harbor"
// or "Eco Power") gets a hardcoded front-of-list position over the others.
// This is only the known-category vocabulary and the one-time seed order for
// a brand-new user's first session -- see categoryOrder/normalizeCategoryOrder
// for the actual (user-draggable, persisted) display order.
const TOOL_CATEGORY_ORDER = [
  "Analysis",
  "Data & Layers",
  "Eco Power",
  "Safe Harbor",
  "Survey Planning",
  "Traffic & Operations",
  "Utilities",
  "Weather & Ocean",
];

// Tools that operate directly on the slip-inventory data model -- everything
// else that used to live under "Safe Harbor Marinas" (Image Generator, DAM,
// GNSS, point-cloud, tides, surfaces, cross-section, project mgmt, etc.) is
// generic and reused across clients (proved by Eco Power's non-marina wells
// using the same project-create API), so it now shows under its native
// category instead.
const SAFE_HARBOR_TOOL_IDS = new Set([
  "slip-delivery",
  "dock-builder",
  "slip-inventory-table",
  "slip-validation",
  "slip-depth-extraction",
  "depth-extraction",
]);

// No Eco Power-specific tools yet -- scaffolded so the first one (methane
// survey evidence tracking, per the BCarbon MEEWP protocol) just needs its
// id added here; empty categories are filtered out of both render sites, so
// this stays invisible until populated.
const ECO_POWER_TOOL_IDS = new Set([]);

// Categories produced by a client-toolset override in listAllTools() rather
// than a tool's own native `category` -- used to give both client toolsets
// the same shared visual treatment (bold/cyan header) wherever they appear.
const CLIENT_TOOLSET_CATEGORIES = new Set(["Safe Harbor", "Eco Power"]);

const DEFAULT_ACCESS_GROUP = "viewer";
const GROUP_TOOL_ACCESS = {
  admin: "all",
  // Inert until a matching Cognito User Pool group ("eco-power") exists and
  // an Eco Power-specific tool id is added here.
  "eco-power": [],
  "safe-harbor-marina": [
    "global-layers",
    "overlay-manager",
    "image-generator",
    "marina-portfolio-manager",
    "digital-asset-manager",
    "slip-delivery",
    "dock-builder",
    "streetview-360",
    "opus-gnss",
    "point-cloud-workspace",
    "tides",
    "datum-calculator",
    "tidal-zoning",
    "gauge-datum-wizard",
    "depth-extraction",
    "surfaces",
    "slip-depth-extraction",
    "slip-inventory-table",
    "slip-validation",
    "cross-section",
    "patch-test",
    "project-management",
    "gantt",
    "travel-planning",
    "remote-data",
    "noaa-coastal-lidar",
  ],
  operator: [
    "global-layers",
    "overlay-manager",
    "image-generator",
    "line-planning",
    "linework",
    "projection-manager",
    "etopo-sounding",
    "satellite-derived-bathymetry",
    "cross-section",
    "contours",
    "change-detection",
    "volume-calculation",
    "realtime-weather",
    "historical-weather",
    "tides",
    "datum-calculator",
    "tidal-zoning",
    "gauge-datum-wizard",
    "coast-pilot-explorer",
    "coastal-webcams",
    "faa-air-traffic",
    "ais-vessel-traffic",
    "point-cloud-workspace",
    "depth-extraction",
    "hydrostack-console",
    "hydrostack-swath",
    "streetview-360",
    "opus-gnss",
    "surfaces",
    "slip-depth-extraction",
    "mbswath-planner",
    "patch-test",
  ],
  viewer: [
    "global-layers",
    "overlay-manager",
    "realtime-weather",
    "historical-weather",
    "tides",
    "datum-calculator",
    "tidal-zoning",
    "gauge-datum-wizard",
    "coast-pilot-explorer",
    "coastal-webcams",
    "faa-air-traffic",
    "ais-vessel-traffic",
    "etopo-sounding",
    "surfaces",
  ],
};

function normalizeGroupKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
}

const TOOL_DEFS = [
  {
    ...dryStorage3dTool,
    category: "Safe Harbor",
    description: "Draw dry stack racks, yard/trailer slots, covered rows and boatyard storage in 3D and quantify inventory. Admin-only pending review.",
  },
  {
    ...gaugeDatumWizardTool,
    category: "Weather & Ocean",
    description: "Import a field tide-gauge CSV, QC it, compare it to a nearby CO-OPS control station, and compute a preliminary local tidal datum with an exportable report.",
  },
  {
    ...tidalZoningTool,
    category: "Weather & Ocean",
    description: "Find NOAA's archived hydrographic surveys near a project area, and build a simplified planning-grade tide corrector between two control stations.",
  },
  {
    ...datumCalculatorTool,
    category: "Weather & Ocean",
    description: "Convert a point or CSV of points between reference frames and tidal datums via NOAA VDatum, or read a nearby CO-OPS station's own datum table.",
  },
  {
    ...systemMapTool,
    category: "Utilities",
    description: "Map of every HydroTools module around the core platform: status, owners, dependencies, roadmap and code paths. Admin-only.",
  },
  {
    ...geoaiTool,
    category: "Analysis",
    description: "GEOAI pilot: chat with live project data, inspect results, and show matching features on the map.",
  },
  {
    ...globalLayersTool,
    category: "Data & Layers",
    description: "Global stack of all layers grouped by category.",
  },
  {
    ...overlayManagerTool,
    category: "Data & Layers",
    description: "Add/import basemaps and overlays.",
  },
  {
    ...imageGeneratorTool,
    category: "Survey Planning",
    description: "Project workspace for planning, reporting, assets, and production context -- includes trip/route planning.",
  },
  {
    ...marinaPortfolioManagerTool,
    category: "Survey Planning",
    description: "Create projects, set map locations, and track portfolio delivery status.",
  },
  {
    ...digitalAssetManagerTool,
    category: "Survey Planning",
    description: "Manage LAS files, GeoTIFFs, 360 photos, project files, and tile pyramids by project.",
  },
  {
    ...projectManagementTool,
    category: "Survey Planning",
    description: "Track project tasks, dates, owners, and delivery status.",
  },
  {
    ...ganttTool,
    category: "Survey Planning",
    description: "Gantt chart view of project schedules.",
  },
  {
    ...slipDeliveryTool,
    category: "Survey Planning",
    description: "Slip inventory records, line estimation, and slip-geometry fitting workflow.",
  },
  {
    ...dockBuilderTool,
    category: "Survey Planning",
    description: "Draw a marina's docks over the imagery -- walkways, fingers, piles, fittings -- as catalog components with rule checks and a bill of materials.",
  },
  {
    ...slipInventoryTableTool,
    category: "Survey Planning",
    description: "Full-screen slip inventory table view.",
  },
  {
    ...slipValidationTool,
    category: "Survey Planning",
    description: "Validate the entire slip inventory database against QC rules and list violations.",
  },
  {
    ...streetView360Tool,
    category: "Survey Planning",
    description: "Import geotagged 360 photos, plot route frames, and navigate a street-view-style panorama.",
  },
  {
    ...photoMosaicTool,
    category: "Survey Planning",
    description: "Index a georeferenced camera-still sequence (e.g. Voyis underwater camera) and plot it as a trail on the map.",
  },
  {
    ...sonarMosaicEditorTool,
    category: "Survey Planning",
    description: "Load MinSAS sonar survey lines, adjust opacity/range-trim per line live, and bake a real mosaic GeoTIFF.",
  },
  {
    ...opusGnssTool,
    category: "Survey Planning",
    description: "Submit GNSS observation files to NGS OPUS for post-processing and capture the emailed solution automatically.",
  },
  {
    ...realtimeWeatherTool,
    category: "Weather & Ocean",
    description: "Load NOAA realtime weather radar and forecast WMS layers.",
  },
  {
    ...historicalWeatherTool,
    category: "Weather & Ocean",
    description: "Query historical weather at a point and render 365-day climatology charts.",
  },
  {
    ...tidesTool,
    category: "Weather & Ocean",
    description: "NOAA tide station lookup with observed, predicted, and high/low series.",
  },
  {
    ...bagS102ConverterTool,
    category: "Data & Layers",
    description: "Generate BAG to S-102 conversion commands for local GDAL + s100py execution.",
  },
  {
    ...qimeraCommandTool,
    category: "Data & Layers",
    description: "Build Qimera 2.8.2 qimera-command workflows as validated commands and JSON configs.",
  },
  {
    ...fmgtControllerTool,
    category: "Data & Layers",
    description: "Scaffold and orchestrate FMGT backscatter mosaic automation jobs from exported GSF inputs.",
  },
  {
    ...pointCloudWorkspaceTool,
    category: "Data & Layers",
    description: "Open local/server point cloud datasets and preview sampled points in an interactive 3D workspace.",
  },
  {
    ...potreeBulkGeneratorTool,
    category: "Utilities",
    description: "Admin: batch-generate an individual Potree project for every tile in a project's GPKG tile index.",
  },
  {
    // Admin-only on hold: deliberately not added to any GROUP_TOOL_ACCESS array below (not
    // safe-harbor-marina, operator, or viewer) -- only the "admin" Cognito group can see this
    // tool. Same mechanism as eco-power's empty [] array, just via omission since there's no
    // per-tool "adminOnly" flag in this file. Don't "fix" this by adding it to a group array.
    ...assistantTool,
    category: "Utilities",
    description: "Admin (on hold): ask questions about project data (slip inventory counts, regions, delivery dates) in plain language.",
  },
  {
    ...depthExtractionTool,
    category: "Analysis",
    description: "Extract depth values from bathymetric LAS/LAZ at slip centroid locations with radius-based sampling.",
  },
  {
    ...patchTestTool,
    category: "Analysis",
    description: "Estimate multibeam roll, pitch, and yaw offsets from overlapping patch-test line residuals.",
  },
  {
    ...surfacesTool,
    category: "Data & Layers",
    description: "Multi-surface catalog with per-surface colormap, hillshade, gamma, and opacity controls.",
  },
  {
    ...slipDepthExtractionTool,
    category: "Analysis",
    description: "Point, transect, and grid soundings from depth raster; populate slip inventory depths.",
  },
  {
    ...hydrostackConsoleTool,
    category: "Data & Layers",
    description: "Hydrostack health, packet counters, and simulated sensor packet controls.",
  },
  {
    ...hydrostackSwathTool,
    category: "Data & Layers",
    description: "Real-time MBES swath waterfall — Canvas2D ping history, per-beam depth coloring, roll stabilization, and motion strip.",
  },
  {
    ...ehydroTool,
    category: "Data & Layers",
    description: "Quick-load USACE eHydro survey and channel framework feature layers.",
  },
  {
    ...remoteDataTool,
    category: "Data & Layers",
    description: "CloudShare field-data inventory — browse the remote manifest, send folders to a project DAM, and run portfolio auto-sync (new GoPro/LAS files land in the mapped project's DAM after each scan).",
  },
  {
    // Admin-only: deliberately not in any GROUP_TOOL_ACCESS array (same mechanism as the Assistant).
    ...fieldCheckTool,
    category: "Data & Layers",
    description: "Admin: check a marina's Recon XT field data on CloudShare (completeness, GNSS/base, lidar coverage of every dock) without Spatial Explorer or Inertial Explorer.",
  },
  {
    ...noaaLidarTool,
    category: "Data & Layers",
    description: "Browse NOAA's public coastal lidar on the map (nightly-synced catalog of every Digital Coast point-cloud dataset) and copy chosen LAZ tiles straight into the active project's DAM.",
  },
  {
    ...encChartsTool,
    category: "Data & Layers",
    description: "Leaflet ENC Direct vector layers with viewport querying and ENC-focused styling.",
  },
  {
    ...travelPlanningTool,
    category: "Survey Planning",
    description: "Point-based per diem, nearest airports, and travel cost planning metrics.",
  },
  {
    ...waterClarityTool,
    category: "Survey Planning",
    description: "Historical inland lake clarity planning scaffold with source catalog and normalized schema.",
  },
  {
    ...coastPilotTool,
    category: "Survey Planning",
    description: "Map-click Coast Pilot excerpts and TripTik-style route guide generation.",
  },
  {
    ...coastalWebcamsTool,
    category: "Weather & Ocean",
    description: "Curated coastal webcam map with preview and watchlist pins.",
  },
  {
    ...forestFiresTool,
    category: "Traffic & Operations",
    description: "NASA FIRMS wildfire hotspot layers.",
  },
  {
    ...floodPlanModelTool,
    category: "Traffic & Operations",
    description: "Flood planning sketch model with water-rise buffers.",
  },
  {
    ...stormSurgeModelTool,
    category: "Traffic & Operations",
    description: "Storm-surge route envelope planner.",
  },
  {
    ...openDriftTool,
    category: "Traffic & Operations",
    description: "First-order drift tracks for spills or floating objects.",
  },
  {
    ...illegalFishingReportsTool,
    category: "Traffic & Operations",
    description: "Illegal fishing incident capture and export.",
  },
  {
    ...fieldLogTool,
    category: "Traffic & Operations",
    description: "Field log generation with geotagged notes.",
  },
  {
    ...vesselLogTool,
    category: "Traffic & Operations",
    description: "Vessel fueling, maintenance, and runtime ledger.",
  },
  {
    ...marineMammalLogTool,
    category: "Traffic & Operations",
    description: "Marine mammal observation log with map points.",
  },
  {
    ...dataTransferTool,
    category: "Traffic & Operations",
    description: "Data transfer job planner (boat to office).",
  },
  {
    ...localNoticeTool,
    category: "Traffic & Operations",
    description: "Draft Local Notice to Mariners bulletins.",
  },
  {
    ...fisheriesInteractionTool,
    category: "Traffic & Operations",
    description: "Seasonal fisheries interaction zoning map.",
  },
  {
    ...s57s101Tool,
    category: "Data & Layers",
    description: "S-57/S-101 GeoJSON viewer and layer management.",
  },
  {
    ...faaAirTrafficTool,
    category: "Traffic & Operations",
    description: "Realtime air traffic with OpenSky polling and ADSB fallback.",
  },
  {
    ...aisVesselTrafficTool,
    category: "Traffic & Operations",
    description: "Realtime AIS vessel traffic from the local Docker-backed database.",
  },
  {
    ...linePlanningTool,
    category: "Survey Planning",
    description: "AOI/centerline planning workflow shell.",
  },
  {
    ...mbswathPlannerTool,
    category: "Survey Planning",
    description: "Draw a survey boundary and generate ETOPO-driven multibeam swath lines with GeoJSON/HYPACK/KMZ export.",
  },
  {
    ...lineworkTool,
    category: "Data & Layers",
    description: "Import, manage, draw, and export vector linework layers.",
  },
  {
    ...projectionManagerTool,
    category: "Data & Layers",
    description: "Manage project CRS profiles and coordinate transforms.",
  },
  {
    ...etopoSoundingTool,
    category: "Analysis",
    description: "Click-to-sample depth/elevation soundings from ETOPO or BlueTopo.",
  },
  {
    ...satelliteDerivedBathymetryTool,
    category: "Analysis",
    description: "Calibrate shallow-water SDB regressions from multiband GeoTIFF imagery and sounding controls.",
  },
  {
    ...crossSectionTool,
    category: "Analysis",
    description: "Draw centerline, generate cross sections, sample ETOPO profiles.",
  },
  {
    ...contoursTool,
    category: "Analysis",
    description: "Draw AOI and generate ETOPO depth contours.",
  },
  {
    ...changeDetectionTool,
    category: "Analysis",
    description: "Analyze XYZ difference grids for shoal/scour change clusters.",
  },
  {
    ...volumeCalculationTool,
    category: "Analysis",
    description: "Compute cut/fill/net volumes between two aligned XYZ surfaces.",
  },
];

function readEnabledIds() {
  try {
    const raw = localStorage.getItem(ENABLED_KEY);
    if (!raw) return [...DEFAULT_ENABLED_TOOL_IDS];
    const ids = JSON.parse(raw);
    if (!Array.isArray(ids)) return [...DEFAULT_ENABLED_TOOL_IDS];
    const migrated = ids.includes("live-traffic")
      ? [...ids.filter((id) => id !== "live-traffic"), "faa-air-traffic", "ais-vessel-traffic"]
      : ids;
    // Dock Builder was split out of Slip Inventory (it was its Docks card): whoever had Slip
    // Inventory gets it beside it, once -- after that, removing it sticks.
    let docked = migrated;
    if (!localStorage.getItem(DOCK_BUILDER_SPLIT_KEY)) {
      localStorage.setItem(DOCK_BUILDER_SPLIT_KEY, "1");
      const at = migrated.indexOf("slip-delivery");
      if (at >= 0 && !migrated.includes("dock-builder")) docked = [...migrated.slice(0, at + 1), "dock-builder", ...migrated.slice(at + 1)];
    }
    const filtered = docked.filter((id) => TOOL_DEFS.some((t) => t.id === id));
    if (!filtered.includes("global-layers")) filtered.unshift("global-layers");
    return filtered;
  } catch {
    return [...DEFAULT_ENABLED_TOOL_IDS];
  }
}

function saveEnabledIds(ids) {
  localStorage.setItem(ENABLED_KEY, JSON.stringify(ids));
}

function readToolOrder() {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

function saveToolOrder(ids) {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(ids));
  } catch {}
}

function readCategoryOrder() {
  try {
    const raw = localStorage.getItem(CATEGORY_ORDER_KEY);
    const names = raw ? JSON.parse(raw) : [];
    return Array.isArray(names) ? names : [];
  } catch {
    return [];
  }
}

function saveCategoryOrder(names) {
  try {
    localStorage.setItem(CATEGORY_ORDER_KEY, JSON.stringify(names));
  } catch {}
}

function defaultDockForTool(id) {
  if (DEFAULT_BOTTOM_DOCK_TOOL_IDS.has(id)) return "bottom";
  return LEFT_DOCK_TOOL_IDS.has(id) ? "left" : "right";
}

function readDockAssignments() {
  try {
    const raw = localStorage.getItem(DOCK_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    const out = {};
    for (const def of TOOL_DEFS) {
      const v = String(parsed[def.id] || "").trim().toLowerCase();
      out[def.id] = ["left", "right", "bottom"].includes(v) ? v : defaultDockForTool(def.id);
    }
    return out;
  } catch {
    const out = {};
    for (const def of TOOL_DEFS) out[def.id] = defaultDockForTool(def.id);
    return out;
  }
}

function saveDockAssignments(assignments) {
  try { localStorage.setItem(DOCK_KEY, JSON.stringify(assignments || {})); } catch {}
}

function readBuilderCategoryOpenState() {
  const out = {};
  TOOL_CATEGORY_ORDER.forEach((category) => {
    out[category] = false;
  });
  return out;
}

function saveBuilderCategoryOpenState(state) {
  try { localStorage.setItem(BUILDER_CATEGORY_OPEN_KEY, JSON.stringify(state || {})); } catch {}
}

function readDockCategoryOpenState() {
  // Always start with dock categories collapsed on app load/reload.
  // Tool enabled/disabled state is persisted separately and remains intact.
  const out = {};
  ["left", "right", "bottom"].forEach((dock) => {
    out[dock] = {};
    TOOL_CATEGORY_ORDER.forEach((category) => {
      out[dock][category] = false;
    });
  });
  return out;
}

function saveDockCategoryOpenState(state) {
  try { localStorage.setItem(DOCK_CATEGORY_OPEN_KEY, JSON.stringify(state || {})); } catch {}
}

function readOpenPanels() {
  try {
    const raw = localStorage.getItem(OPEN_PANELS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function saveOpenPanels(set) {
  try { localStorage.setItem(OPEN_PANELS_KEY, JSON.stringify([...set])); } catch {}
}

export function loadTools(appContext) {
  const userGroups = Array.isArray(appContext?.auth?.groups) && appContext.auth.groups.length
    ? appContext.auth.groups.map((g) => normalizeGroupKey(g)).filter(Boolean)
    : [DEFAULT_ACCESS_GROUP];
  const isAdmin = userGroups.includes("admin");
  const allowedToolIds = new Set(
    isAdmin
      ? TOOL_DEFS.map((t) => t.id)
      : userGroups.flatMap((group) => {
          const mapped = GROUP_TOOL_ACCESS[group];
          if (!mapped || mapped === "all") return [];
          return Array.isArray(mapped) ? mapped : [];
        })
  );
  allowedToolIds.add("global-layers");
  const allById = new Map(TOOL_DEFS.map((t) => [t.id, t]));
  const enabled = new Set(readEnabledIds());
  let toolOrder = readToolOrder();
  let categoryOrder = readCategoryOrder();
  let dockAssignments = readDockAssignments();
  const active = new Set();
  const openPanels = new Set(readOpenPanels());
  let hasBottomDockHost = false;
  let builderButtonEl = null;
  let builderMenuOpen = false;
  let builderMenuScrollTop = 0;
  let builderFilterText = "";
  let builderFilterSelectionStart = 0;
  let builderFilterSelectionEnd = 0;
  let builderCategoryOpen = readBuilderCategoryOpenState();
  let dockCategoryOpen = readDockCategoryOpenState();
  let suppressSummaryToggleUntil = 0;
  const initializedPanelSubsets = new Set();
  const panelDetailOpenState = new Map();

  function escapeBuilderText(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function closeBuilderMenu() {
    const body = document.querySelector("#dashboard-builder-popover .dashboard-builder-popover-body");
    if (body) builderMenuScrollTop = body.scrollTop || 0;
    builderMenuOpen = false;
    const overlay = document.getElementById("dashboard-builder-overlay");
    if (overlay?.parentElement) overlay.parentElement.removeChild(overlay);
  }

  function renderBuilderMenu() {
    if (!builderMenuOpen) {
      closeBuilderMenu();
      return;
    }
    const existingBody = document.querySelector("#dashboard-builder-popover .dashboard-builder-popover-body");
    if (existingBody) builderMenuScrollTop = existingBody.scrollTop || 0;
    const tools = listAllTools();
    const filterNeedle = String(builderFilterText || "").trim().toLowerCase();
    const filteredTools = tools.filter((tool) => {
      if (!filterNeedle) return true;
      const haystack = [tool.name, tool.category, tool.description].join(" ").toLowerCase();
      return haystack.includes(filterNeedle);
    });
    const groupedTools = new Map(categoryOrder.map((category) => [category, []]));
    filteredTools.forEach((tool) => {
      if (!groupedTools.has(tool.category)) groupedTools.set(tool.category, []);
      groupedTools.get(tool.category).push(tool);
    });
    // Client toolsets (Safe Harbor, Eco Power) always render as their own
    // separated block here, ahead of "Core Tools" -- unlike the dock
    // sidebar (where the user can freely drag categories into any order),
    // this popover is a categorized picker, so grouping by toolset stays
    // structural rather than following categoryOrder's freeform sequence.
    // categoryOrder still governs relative order *within* each block.
    const categoryDetailsHtml = (category, items) => `
        <details class="toolset-group dashboard-builder-category" data-builder-category="${escapeBuilderText(category)}" ${builderCategoryOpen[category] ? "open" : ""}>
          <summary>${category} <span class="small">(${items.length})</span></summary>
          <div class="toolset-content">
            ${items.map((tool) => `
              <label class="toolset-row">
                <div class="toolset-meta">
                  <div class="toolset-name">${tool.name}</div>
                  <div class="small">${tool.description}</div>
                </div>
                <input type="checkbox" data-tool-toggle="${tool.id}" ${tool.enabled ? "checked" : ""} />
              </label>
            `).join("")}
          </div>
        </details>
      `;
    const nonEmptyEntries = [...groupedTools.entries()].filter(([, items]) => items.length);
    const clientRows = nonEmptyEntries
      .filter(([category]) => CLIENT_TOOLSET_CATEGORIES.has(category))
      .map(([category, items]) => categoryDetailsHtml(category, items))
      .join("");
    const coreRows = nonEmptyEntries
      .filter(([category]) => !CLIENT_TOOLSET_CATEGORIES.has(category))
      .map(([category, items]) => categoryDetailsHtml(category, items))
      .join("");
    const chooserRows = [
      clientRows,
      coreRows ? `<div class="toolset-section-header">Core Tools</div>${coreRows}` : "",
    ].join("");
    let overlay = document.getElementById("dashboard-builder-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "dashboard-builder-overlay";
      overlay.className = "settings-backdrop";
      overlay.setAttribute("aria-hidden", "false");
      document.getElementById("app")?.appendChild(overlay);
    }
    overlay.setAttribute("aria-hidden", "false");
    overlay.innerHTML = `
      <div class="settings-modal dashboard-builder-modal" role="dialog" aria-modal="true" aria-labelledby="dashboard-builder-title">
        <div class="settings-head">
          <div id="dashboard-builder-title" class="settings-title">Dashboard Builder</div>
          <button type="button" class="iconbtn" id="dashboard-builder-close" title="Close">x</button>
        </div>
        <div class="dashboard-builder-filter">
          <input
            id="dashboard-builder-filter"
            type="text"
            placeholder="Filter dashboards by name"
            value="${escapeBuilderText(builderFilterText)}"
            autocomplete="off"
          />
        </div>
        <div id="dashboard-builder-popover" class="dashboard-builder-popover">
          <div class="dashboard-builder-popover-body">${chooserRows || `<div class="small" style="padding:6px 2px;">No dashboards match that filter.</div>`}</div>
        </div>
      </div>
    `;
    const menu = overlay.querySelector("#dashboard-builder-popover");
    overlay.querySelector("#dashboard-builder-close")?.addEventListener("click", closeBuilderMenu);
    overlay.querySelector("#dashboard-builder-filter")?.addEventListener("input", (e) => {
      builderFilterText = e.target?.value || "";
      builderFilterSelectionStart = e.target?.selectionStart ?? builderFilterText.length;
      builderFilterSelectionEnd = e.target?.selectionEnd ?? builderFilterText.length;
      builderMenuScrollTop = 0;
      renderBuilderMenu();
    });
    menu.onchange = (e) => {
      const input = e.target;
      const id = input?.dataset?.toolToggle;
      if (!id) return;
      setToolEnabled(id, !!input.checked);
    };
    menu.querySelectorAll("details[data-builder-category]").forEach((details) => {
      details.addEventListener("toggle", () => {
        const category = String(details.dataset.builderCategory || "");
        if (!category) return;
        builderCategoryOpen[category] = details.open;
        saveBuilderCategoryOpenState(builderCategoryOpen);
      });
    });
    const body = overlay.querySelector(".dashboard-builder-popover-body");
    if (body) {
      body.scrollTop = builderMenuScrollTop;
      body.addEventListener("scroll", () => {
        builderMenuScrollTop = body.scrollTop || 0;
      }, { passive: true });
    }
    const filterInput = overlay.querySelector("#dashboard-builder-filter");
    if (filterInput) {
      filterInput.focus();
      const nextStart = Math.max(0, Math.min(builderFilterSelectionStart, filterInput.value.length));
      const nextEnd = Math.max(nextStart, Math.min(builderFilterSelectionEnd, filterInput.value.length));
      filterInput.setSelectionRange(nextStart, nextEnd);
    }
  }

  function ensureBuilderTopbarButton() {
    if (builderButtonEl) return;
    const wrap = document.querySelector("#topbar");
    if (!wrap) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "dashboard-builder-btn";
    btn.className = "btn";
    btn.textContent = "Dashboard Builder";
    btn.addEventListener("click", () => {
      builderMenuOpen = !builderMenuOpen;
      renderBuilderMenu();
    });
    wrap.appendChild(btn);
    builderButtonEl = btn;
  }

  function normalizeToolOrder() {
    const valid = TOOL_DEFS.map((t) => t.id);
    const filtered = toolOrder.filter((id) => valid.includes(id));
    valid.forEach((id) => {
      if (!filtered.includes(id)) filtered.push(id);
    });
    toolOrder = filtered;
    saveToolOrder(toolOrder);
  }

  function normalizeCategoryOrder() {
    const valid = TOOL_CATEGORY_ORDER;
    const filtered = categoryOrder.filter((name) => valid.includes(name));
    valid.forEach((name) => {
      if (!filtered.includes(name)) filtered.push(name);
    });
    categoryOrder = filtered;
    saveCategoryOrder(categoryOrder);
  }

  function normalizeDockAssignments() {
    const next = {};
    for (const def of TOOL_DEFS) {
      const v = String(dockAssignments?.[def.id] || "").trim().toLowerCase();
      next[def.id] = ["left", "right", "bottom"].includes(v) ? v : defaultDockForTool(def.id);
    }
    dockAssignments = next;
    saveDockAssignments(dockAssignments);
  }

  function resolveRequestedDock(id) {
    return dockAssignments[id] || defaultDockForTool(id);
  }

  function resolveEffectiveDock(id) {
    const requested = resolveRequestedDock(id);
    if (requested === "bottom" && BOTTOM_OWNER_TOOL_IDS.has(id)) return "right";
    return requested;
  }

  function inferDockForNewTool(id) {
    const def = allById.get(id);
    if (!def?.category) return defaultDockForTool(id);
    const categoryDocks = TOOL_DEFS
      .filter((tool) => tool.id !== id && tool.category === def.category && enabled.has(tool.id))
      .map((tool) => resolveEffectiveDock(tool.id));
    const sideDocks = [...new Set(categoryDocks.filter((dock) => dock === "left" || dock === "right"))];
    if (sideDocks.length === 1) return sideDocks[0];
    const allDocks = [...new Set(categoryDocks)];
    return allDocks.length === 1 ? allDocks[0] : defaultDockForTool(id);
  }

  function reconcileDockAssignmentOnEnable(id) {
    const inferredDock = inferDockForNewTool(id);
    const currentDock = dockAssignments[id];
    const defaultDock = defaultDockForTool(id);
    if (!currentDock) {
      dockAssignments[id] = inferredDock;
      saveDockAssignments(dockAssignments);
      return;
    }
    if (currentDock === inferredDock) return;
    // Preserve tools with a non-side default dock (for example Image Generator
    // in the bottom dock) when they are re-enabled from the builder.
    if (!["left", "right"].includes(defaultDock) && currentDock === defaultDock) return;
    if (currentDock === defaultDock && inferredDock !== defaultDock) {
      dockAssignments[id] = inferredDock;
      saveDockAssignments(dockAssignments);
    }
  }

  function listAllTools() {
    return TOOL_DEFS.map(({ id, name, category, description }) => ({
      id,
      name,
      category: SAFE_HARBOR_TOOL_IDS.has(id) ? "Safe Harbor"
        : ECO_POWER_TOOL_IDS.has(id) ? "Eco Power"
        : category,
      description,
      enabled: enabled.has(id),
      dock: resolveEffectiveDock(id),
    })).filter((tool) => allowedToolIds.has(tool.id));
  }

  function persistEnabled() {
    saveEnabledIds(TOOL_DEFS.map((t) => t.id).filter((id) => enabled.has(id)));
  }

  function panelDetailStateKey(details, index) {
    const dataset = details?.dataset
      ? Object.entries(details.dataset)
          .filter(([key]) => key !== "panelId")
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => `${key}:${value}`)
          .join("|")
      : "";
    const id = details?.id ? `id:${details.id}` : "";
    const summary = details?.querySelector?.(":scope > summary")?.textContent
      ?.trim()
      ?.replace(/\s+/g, " ")
      ?.slice(0, 120) || "";
    return [id, dataset, summary ? `summary:${summary}` : "", `idx:${index}`].filter(Boolean).join("||");
  }

  function capturePanelDetailOpenState() {
    document.querySelectorAll("[id^='tool-panel-']").forEach((panelEl) => {
      const toolId = String(panelEl.id || "").replace(/^tool-panel-/, "");
      if (!toolId) return;
      const openKeys = new Set();
      panelEl.querySelectorAll("details").forEach((details, index) => {
        if (details.open) openKeys.add(panelDetailStateKey(details, index));
      });
      panelDetailOpenState.set(toolId, openKeys);
    });
  }

  function restorePanelDetailOpenState(toolId, panelEl) {
    const openKeys = panelDetailOpenState.get(toolId);
    if (!openKeys) return;
    panelEl.querySelectorAll("details").forEach((details, index) => {
      details.open = openKeys.has(panelDetailStateKey(details, index));
    });
  }

  function detachRenderedToolPanels() {
    const panels = new Map();
    document.querySelectorAll("[id^='tool-panel-']").forEach((panelEl) => {
      const toolId = String(panelEl.id || "").replace(/^tool-panel-/, "");
      if (!toolId) return;
      panelEl.parentElement?.removeChild(panelEl);
      panels.set(toolId, panelEl);
    });
    return panels;
  }

  function captureOpenPanels() {
    const roots = [document.getElementById("toolset-sidebar"), document.getElementById("layers-sidebar"), document.getElementById("bottom-toolset-sidebar")].filter(Boolean);
    if (!roots.length) return;
    capturePanelDetailOpenState();
    openPanels.clear();
    roots.forEach((root) => {
      root.querySelectorAll("details[data-panel-id]").forEach((d) => {
        const id = d.dataset.panelId;
        if (!id) return;
        if (id.startsWith("dock-category:")) {
          const [, dockName, ...categoryParts] = id.split(":");
          const category = categoryParts.join(":");
          if (!dockName || !category) return;
          if (!dockCategoryOpen[dockName]) dockCategoryOpen[dockName] = {};
          dockCategoryOpen[dockName][category] = d.open;
          return;
        }
        if (!id.startsWith("tool:")) return;
        if (d.open) openPanels.add(id);
      });
    });
    saveDockCategoryOpenState(dockCategoryOpen);
    saveOpenPanels(openPanels);
  }

  function enableTool(id) {
    if (!allowedToolIds.has(id)) return false;
    if (active.has(id)) return true;
    const tool = allById.get(id);
    if (!tool) return false;
    try {
      performance.mark(`tool:init:start:${id}`);
      tool.init?.(appContext);
      performance.mark(`tool:init:end:${id}`);
      performance.measure(`tool:init:${id}`, `tool:init:start:${id}`, `tool:init:end:${id}`);
      active.add(id);
      appContext.log(`Tool loaded: ${id}`);
      return true;
    } catch (e) {
      console.error(`[Tool:${id}] init failed`, e);
      appContext.ui.toast(`Tool failed: ${tool.name}`);
      return false;
    }
  }

  function setToolPanelOpen(id, open) {
    if (!id || !active.has(id)) return;
    const tool = allById.get(id);
    const isOpen = !!open;
    try {
      tool?.setPanelOpen?.(appContext, isOpen);
    } catch (e) {
      console.error("[Tool:" + id + "] panel visibility hook failed", e);
    }
    try {
      appContext.events.emit("tools:panel-visibility", { toolId: id, open: isOpen });
    } catch {}
  }

  function disableTool(id) {
    if (!active.has(id)) return;
    setToolPanelOpen(id, false);
    const tool = allById.get(id);
    try {
      tool?.destroy?.();
    } catch (e) {
      console.error(`[Tool:${id}] destroy failed`, e);
    }
    try {
      appContext.services.layers?.removeLayersByTool?.(id);
    } catch (e) {
      console.error(`[Tool:${id}] layer cleanup failed`, e);
    }
    active.delete(id);
    appContext.log(`Tool unloaded: ${id}`);
  }

  function setToolEnabled(id, shouldEnable, { silent = false } = {}) {
    if (!allById.has(id)) return false;
    if (!allowedToolIds.has(id)) return false;
    const next = !!shouldEnable;
    const isEnabled = enabled.has(id);
    if (next === isEnabled) return true;
    captureOpenPanels();

    if (next) {
      const ok = enableTool(id);
      if (!ok) return false;
      enabled.add(id);
      reconcileDockAssignmentOnEnable(id);
      if (!toolOrder.includes(id)) {
        toolOrder.push(id);
        saveToolOrder(toolOrder);
      }
      if (!silent) appContext.ui.toast(`Enabled: ${allById.get(id).name}`);
    } else {
      disableTool(id);
      enabled.delete(id);
      if (!silent) appContext.ui.toast(`Disabled: ${allById.get(id).name}`);
    }

    persistEnabled();
    renderSidebar();
    appContext.events.emit("tools:changed", { tools: listAllTools() });
    return true;
  }

  function exportProjectData(options = {}) {
    const toolStates = {};
    const toolStateMeta = {};
    for (const def of TOOL_DEFS) {
      if (!enabled.has(def.id)) continue;
      if (!def.serialize) continue;
      try {
        const state = def.serialize(appContext, options);
        if (state !== undefined) {
          toolStates[def.id] = state;
          toolStateMeta[def.id] = {
            version: Number.isInteger(def.stateVersion) ? def.stateVersion : DEFAULT_TOOL_STATE_VERSION,
          };
        }
      } catch (e) {
        console.error(`[Tool:${def.id}] serialize failed`, e);
      }
    }
    return {
      enabledToolIds: TOOL_DEFS.map((t) => t.id).filter((id) => enabled.has(id)),
      toolOrder: [...toolOrder],
      toolStates,
      toolStateMeta,
    };
  }

  function importProjectData(data = {}, options = {}) {
    const report = {
      restored: 0,
      unresolved: [],
      errors: [],
      byTool: {},
    };
    const wanted = new Set(Array.isArray(data.enabledToolIds) ? data.enabledToolIds : []);
    wanted.add("global-layers");
    TOOL_DEFS.forEach((def) => {
      const shouldEnable = wanted.has(def.id);
      const isEnabled = enabled.has(def.id);
      if (shouldEnable !== isEnabled) setToolEnabled(def.id, shouldEnable, { silent: true });
    });

    if (Array.isArray(data.toolOrder) && data.toolOrder.length) {
      toolOrder = data.toolOrder.slice();
      normalizeToolOrder();
      renderSidebar();
    }

    const states = data.toolStates && typeof data.toolStates === "object" ? data.toolStates : {};
    const stateMeta = data.toolStateMeta && typeof data.toolStateMeta === "object" ? data.toolStateMeta : {};
    Object.entries(states).forEach(([id, state]) => {
      const def = allById.get(id);
      if (!def?.restore) return;
      if (!enabled.has(id)) return;
      try {
        const fromVersion = Number(stateMeta?.[id]?.version);
        const toVersion = Number.isInteger(def.stateVersion) ? def.stateVersion : DEFAULT_TOOL_STATE_VERSION;
        const migrated = def.migrateState
          ? def.migrateState(state, {
              fromVersion: Number.isInteger(fromVersion) ? fromVersion : DEFAULT_TOOL_STATE_VERSION,
              toVersion,
            })
          : state;
        const toolReport = def.restore(appContext, migrated, options) || {};
        const restored = Number(toolReport.restored || 0);
        const unresolved = Array.isArray(toolReport.unresolved) ? toolReport.unresolved : [];
        const errors = Array.isArray(toolReport.errors) ? toolReport.errors : [];
        report.byTool[id] = {
          restored,
          unresolved,
          errors,
        };
        report.restored += restored;
        report.unresolved.push(...unresolved.map((u) => ({ ...u, toolId: u.toolId || id })));
        report.errors.push(...errors.map((e) => ({ ...e, toolId: e.toolId || id })));
      } catch (e) {
        console.error(`[Tool:${id}] restore failed`, e);
        report.errors.push({ toolId: id, message: e?.message || String(e) });
      }
    });
    return report;
  }

  async function resolveProjectReference(ref, file) {
    if (!ref || typeof ref !== "object") return { ok: false, message: "Invalid reference" };
    const toolId = String(ref.toolId || "");
    const referenceId = String(ref.referenceId || "");
    if (!toolId || !referenceId) return { ok: false, message: "Missing reference info" };
    const def = allById.get(toolId);
    if (!def?.resolveReference) return { ok: false, message: `Tool ${toolId} cannot relink references` };
    try {
      const out = await def.resolveReference(appContext, referenceId, file, ref);
      return out && typeof out === "object" ? { ok: true, ...out } : { ok: true };
    } catch (e) {
      console.error(`[Tool:${toolId}] resolve reference failed`, e);
      return { ok: false, message: e?.message || String(e) };
    }
  }

  function renderSidebar() {
    ensureBuilderTopbarButton();
    const tools = listAllTools();
    const enabledTools = tools.filter((t) => t.enabled);
    const orderIndex = new Map(toolOrder.map((id, idx) => [id, idx]));
    const sortedEnabled = enabledTools
      .slice()
      .sort((a, b) => (orderIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER));
    const leftEnabled = sortedEnabled.filter((t) => resolveEffectiveDock(t.id) === "left");
    const rightEnabled = sortedEnabled.filter((t) => resolveEffectiveDock(t.id) === "right");
    const bottomEnabled = sortedEnabled.filter((t) => resolveEffectiveDock(t.id) === "bottom");
    const preservedToolPanels = detachRenderedToolPanels();

    const toolCardMarkup = (tool) => `
      <details class="toolset-group" data-tool-id="${tool.id}" data-panel-id="tool:${tool.id}" ${openPanels.has(`tool:${tool.id}`) ? "open" : ""}>
        <summary draggable="true" data-tool-drag-handle="1" data-tool-id="${tool.id}">${tool.name}</summary>
        <div class="toolset-content" id="tool-panel-${tool.id}"></div>
      </details>
    `;
    const toolDetails = (list, emptyMsg, dockName) => {
      if (!list.length) return `<div class="small">${emptyMsg}</div>`;
      const grouped = new Map(categoryOrder.map((category) => [category, []]));
      list.forEach((tool) => {
        if (!grouped.has(tool.category)) grouped.set(tool.category, []);
        grouped.get(tool.category).push(tool);
      });
      return [...grouped.entries()]
        .filter(([, toolsInCategory]) => toolsInCategory.length)
        .map(([category, toolsInCategory]) => `
          <details
            class="toolset-group dock-category-group"
            data-dock-category="${category}"
            data-dock-name="${dockName}"
            data-panel-id="dock-category:${dockName}:${category}"
            ${dockCategoryOpen?.[dockName]?.[category] ? "open" : ""}
          >
            <summary draggable="true" data-category-drag-handle="1" data-dock-name="${dockName}" data-dock-category="${category}" ${CLIENT_TOOLSET_CATEGORIES.has(category) ? 'style="color:#38bdf8; font-weight:700;"' : ""}>${category} <span class="small">(${toolsInCategory.length})</span></summary>
            <div class="toolset-content dock-category-content">
              ${toolsInCategory.map((tool) => toolCardMarkup(tool)).join("")}
            </div>
          </details>
        `)
        .join("");
    };

    appContext.ui.mountLeftPanel(`
      <div id="layers-sidebar" data-dock-root="left">
        ${toolDetails(leftEnabled, "No enabled toolsets docked left.", "left")}
        <div class="tool-drop-end-zone" data-tool-drop-end="1" aria-hidden="true"></div>
      </div>
    `);

    appContext.ui.mountRightPanel(`
      <div id="toolset-sidebar" data-dock-root="right">
        ${toolDetails(rightEnabled, "No enabled toolsets docked right.", "right")}
        <div class="tool-drop-end-zone" data-tool-drop-end="1" aria-hidden="true"></div>
      </div>
    `);

    appContext.ui.mountBottomPanel(`
      <div id="bottom-toolset-sidebar" data-dock-root="bottom">
        ${bottomEnabled.length
          ? toolDetails(bottomEnabled, "", "bottom")
          : `<div class="small" style="padding:10px 16px; color:var(--muted); text-align:center;">Drag tools here</div>`
        }
        <div class="tool-drop-end-zone" data-tool-drop-end="1" aria-hidden="true"></div>
      </div>
    `, { ownerId: "dock-host", title: "Toolsets" });
    hasBottomDockHost = true;

    const leftRoot = document.getElementById("layers-sidebar");
    const rightRoot = document.getElementById("toolset-sidebar");
    const bottomRoot = document.getElementById("bottom-toolset-sidebar");
    const dockRoots = [leftRoot, rightRoot, bottomRoot].filter(Boolean);
    const dockRootByName = new Map(dockRoots.map((r) => [String(r.dataset?.dockRoot || ""), r]));

    dockRoots.forEach((root) => {
      root.addEventListener("click", (e) => {
        if (Date.now() >= suppressSummaryToggleUntil) return;
        const summary = e.target?.closest?.("summary");
        if (!summary) return;
        e.preventDefault();
        e.stopPropagation();
      }, true);
      root.addEventListener("toggle", (e) => {
        const details = e.target;
        if (!details?.matches?.("details[data-panel-id]")) return;
        const id = details.dataset.panelId;
        if (!id) return;
        if (id.startsWith("dock-category:")) {
          const [, dockName, ...categoryParts] = id.split(":");
          const category = categoryParts.join(":");
          if (!dockName || !category) return;
          if (!dockCategoryOpen[dockName]) dockCategoryOpen[dockName] = {};
          dockCategoryOpen[dockName][category] = details.open;
          saveDockCategoryOpenState(dockCategoryOpen);
          return;
        }
        if (details.open) openPanels.add(id);
        else openPanels.delete(id);
        saveOpenPanels(openPanels);
        const toolId = id.slice("tool:".length);
        setToolPanelOpen(toolId, details.open);
      });
    });

    let draggingToolId = null;
    let draggingCategory = null;
    let dragSourceEl = null;
    let dragPlaceholderEl = null;
    let dragGhostEl = null;
    let dropVisualState = null;
    const ensurePlaceholder = () => {
      if (dragPlaceholderEl) return dragPlaceholderEl;
      const el = document.createElement("div");
      el.className = "tool-drag-placeholder";
      dragPlaceholderEl = el;
      return dragPlaceholderEl;
    };
    const clearDropHints = () => {
      dockRoots.forEach((root) => root.querySelectorAll(".tool-drag-over-before, .tool-drag-over-after").forEach((el) => {
        el.classList.remove("tool-drag-over-before", "tool-drag-over-after");
      }));
    };
    const clearDropVisualState = () => {
      dropVisualState = null;
    };
    const clearDragState = () => {
      clearDropHints();
      if (dragPlaceholderEl?.parentElement) dragPlaceholderEl.parentElement.removeChild(dragPlaceholderEl);
      if (dragSourceEl) {
        dragSourceEl.classList.remove("tool-dragging", "tool-drag-source-hidden", "tool-drag-source-collapsed");
      }
      if (dragGhostEl?.parentElement) dragGhostEl.parentElement.removeChild(dragGhostEl);
      dragGhostEl = null;
      dragPlaceholderEl = null;
      dragSourceEl = null;
      draggingToolId = null;
      draggingCategory = null;
      clearDropVisualState();
    };
    const getVisibleDropCards = (dockRoot) => [...(dockRoot?.querySelectorAll?.("details[data-tool-id]") || [])].filter((d) => {
      if (d === dragSourceEl) return false;
      const rect = d.getBoundingClientRect?.();
      return d.getClientRects?.().length > 0 && Number(rect?.height || 0) > 0;
    });
    const placePlaceholder = (dockRoot, target = null, after = false) => {
      const ph = ensurePlaceholder();
      ph.classList.remove("tool-drag-placeholder-hidden");
      ph.dataset.dropDock = String(dockRoot?.dataset?.dockRoot || "");
      if (!dockRoot) return;
      if (target && target.matches?.("details[data-tool-id]")) {
        const alreadyBefore = !after && ph.nextElementSibling === target && ph.parentElement === target.parentElement;
        const alreadyAfter = after && ph.previousElementSibling === target && ph.parentElement === target.parentElement;
        if (alreadyBefore || alreadyAfter) return;
        if (after) target.insertAdjacentElement("afterend", ph);
        else target.insertAdjacentElement("beforebegin", ph);
        return;
      }
      const tail = getVisibleDropCards(dockRoot).pop();
      if (tail) {
        const alreadyAtTail = ph.previousElementSibling === tail && ph.parentElement === tail.parentElement;
        if (!alreadyAtTail) tail.insertAdjacentElement("afterend", ph);
      } else if (ph.parentElement !== dockRoot || ph !== dockRoot.lastElementChild) {
        dockRoot.appendChild(ph);
      }
    };
    const animateDockReflow = (roots, mutate) => {
      const rootList = [...new Set((Array.isArray(roots) ? roots : [roots]).filter(Boolean))];
      const firstRects = new Map();
      rootList.forEach((root) => {
        root.querySelectorAll("details[data-tool-id]").forEach((el) => {
          if (el === dragSourceEl) return;
          firstRects.set(el, el.getBoundingClientRect());
        });
      });
      mutate?.();
      rootList.forEach((root) => {
        root.querySelectorAll("details[data-tool-id]").forEach((el) => {
          if (el === dragSourceEl) return;
          const first = firstRects.get(el);
          if (!first) return;
          const last = el.getBoundingClientRect();
          const dx = first.left - last.left;
          const dy = first.top - last.top;
          if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
          el.style.transition = "none";
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          void el.getBoundingClientRect();
          el.style.transition = "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)";
          el.style.transform = "";
          el.addEventListener("transitionend", () => {
            el.style.transition = "";
          }, { once: true });
        });
      });
    };
    const setDropVisual = (dockRoot, target = null, after = false) => {
      const nextState = {
        dock: String(dockRoot?.dataset?.dockRoot || ""),
        targetId: target?.dataset?.toolId || "",
        after: !!after,
      };
      if (
        dropVisualState
        && dropVisualState.dock === nextState.dock
        && dropVisualState.targetId === nextState.targetId
        && dropVisualState.after === nextState.after
      ) return;
      const prevRoot = dropVisualState?.dock ? dockRootByName.get(dropVisualState.dock) : null;
      animateDockReflow([prevRoot, dockRoot], () => {
        clearDropHints();
        placePlaceholder(dockRoot, target, after);
      });
      dropVisualState = nextState;
    };
    const getPointerDropState = (dockRoot, clientY, onEndZone = false) => {
      const dropDock = String(dockRoot?.dataset?.dockRoot || "");
      const cards = getVisibleDropCards(dockRoot);
      if (!cards.length) return { dock: dropDock, targetEl: null, targetId: "", after: false };
      if (onEndZone) {
        const tail = cards[cards.length - 1];
        return { dock: dropDock, targetEl: tail, targetId: tail?.dataset?.toolId || "", after: true };
      }
      for (const card of cards) {
        const r = card.getBoundingClientRect();
        const mid = r.top + (r.height / 2);
        if (clientY < mid) {
          return { dock: dropDock, targetEl: card, targetId: card?.dataset?.toolId || "", after: false };
        }
      }
      const tail = cards[cards.length - 1];
      return { dock: dropDock, targetEl: tail, targetId: tail?.dataset?.toolId || "", after: true };
    };

    const getVisibleCategoryCards = (dockRoot) => [...(dockRoot?.querySelectorAll?.("details[data-dock-category]") || [])].filter((d) => {
      if (d === dragSourceEl) return false;
      const rect = d.getBoundingClientRect?.();
      return d.getClientRects?.().length > 0 && Number(rect?.height || 0) > 0;
    });
    // Same-dock category reorder target: unlike getPointerDropState (tool cards),
    // this only needs to answer "which category card, before or after" -- there's
    // no end-zone/placeholder-element machinery for category cards (see plan: no
    // animated reflow preview for this, just a before/after highlight on drop).
    const getCategoryPointerDropState = (dockRoot, clientY) => {
      const cards = getVisibleCategoryCards(dockRoot);
      if (!cards.length) return { targetEl: null, targetCategory: "", after: false };
      for (const card of cards) {
        const r = card.getBoundingClientRect();
        const mid = r.top + (r.height / 2);
        if (clientY < mid) {
          return { targetEl: card, targetCategory: card?.dataset?.dockCategory || "", after: false };
        }
      }
      const tail = cards[cards.length - 1];
      return { targetEl: tail, targetCategory: tail?.dataset?.dockCategory || "", after: true };
    };

    const applyDrop = (dropDock, targetId = null, after = false) => {
      if (!draggingToolId || !allById.has(draggingToolId)) return;
      dockAssignments[draggingToolId] = dropDock;
      const next = toolOrder.filter((id) => id !== draggingToolId);
      if (targetId && targetId !== draggingToolId && next.includes(targetId)) {
        let at = next.indexOf(targetId);
        if (after) at += 1;
        next.splice(Math.max(0, Math.min(at, next.length)), 0, draggingToolId);
      } else {
        const lastInDock = next.map((id, idx) => ({ id, idx })).filter(({ id }) => resolveEffectiveDock(id) === dropDock).pop();
        const at = lastInDock ? (lastInDock.idx + 1) : next.length;
        next.splice(Math.max(0, Math.min(at, next.length)), 0, draggingToolId);
      }
      toolOrder = next;
      normalizeToolOrder();
      normalizeDockAssignments();
      saveDockAssignments(dockAssignments);
      captureOpenPanels();
      renderSidebar();
    };

    const applyCategoryDrop = (dropDock) => {
      if (!draggingCategory || !["left", "right", "bottom"].includes(dropDock)) return;
      const { category, fromDock } = draggingCategory;
      if (!category || !fromDock || fromDock === dropDock) return;
      const categoryToolIds = sortedEnabled
        .filter((tool) => resolveEffectiveDock(tool.id) === fromDock && tool.category === category)
        .map((tool) => tool.id)
        .filter((id) => allById.has(id));
      if (!categoryToolIds.length) return;
      categoryToolIds.forEach((id) => {
        dockAssignments[id] = dropDock;
      });
      normalizeDockAssignments();
      saveDockAssignments(dockAssignments);
      captureOpenPanels();
      renderSidebar();
    };

    const applyCategoryReorder = (category, targetCategory, after = false) => {
      if (!category || !categoryOrder.includes(category)) return;
      const next = categoryOrder.filter((name) => name !== category);
      if (targetCategory && targetCategory !== category && next.includes(targetCategory)) {
        let at = next.indexOf(targetCategory);
        if (after) at += 1;
        next.splice(Math.max(0, Math.min(at, next.length)), 0, category);
      } else {
        next.push(category);
      }
      categoryOrder = next;
      saveCategoryOrder(categoryOrder);
      captureOpenPanels();
      renderSidebar();
    };

    dockRoots.forEach((root) => {
      root.addEventListener("dragstart", (e) => {
        const categoryHandle = e.target?.closest?.('summary[data-category-drag-handle="1"]');
        if (categoryHandle) {
          const category = String(categoryHandle.dataset.dockCategory || "");
          const fromDock = String(categoryHandle.dataset.dockName || "");
          if (!category || !["left", "right"].includes(fromDock)) return;
          draggingCategory = { category, fromDock };
          dragSourceEl = categoryHandle.parentElement?.closest?.("details[data-dock-category]") || null;
          dragSourceEl?.classList?.add("tool-dragging");
          try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", `category:${fromDock}:${category}`);
            e.dataTransfer.dropEffect = "move";
            if (dragSourceEl) {
              const rect = dragSourceEl.getBoundingClientRect();
              const ghost = dragSourceEl.cloneNode(true);
              ghost.classList.add("tool-drag-ghost");
              ghost.style.width = `${Math.round(rect.width)}px`;
              ghost.style.height = `${Math.round(rect.height)}px`;
              ghost.style.position = "fixed";
              ghost.style.left = "-10000px";
              ghost.style.top = "-10000px";
              ghost.style.pointerEvents = "none";
              document.body.appendChild(ghost);
              dragGhostEl = ghost;
              const offsetX = Math.max(8, Math.min(rect.width - 8, (e.clientX || rect.left) - rect.left));
              const offsetY = Math.max(8, Math.min(rect.height - 8, (e.clientY || rect.top) - rect.top));
              e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
            }
          } catch {}
          requestAnimationFrame(() => {
            if (!dragSourceEl || !draggingCategory) return;
            dragSourceEl.classList.add("tool-drag-source-collapsed");
          });
          return;
        }
        const handle = e.target?.closest?.('summary[data-tool-drag-handle="1"]');
        if (!handle) return;
        draggingToolId = handle.dataset.toolId || handle.parentElement?.dataset?.toolId || null;
        if (!draggingToolId) return;
        const details = root.querySelector(`details[data-tool-id="${draggingToolId}"]`);
        dragSourceEl = details || null;
        dragSourceEl?.classList?.add("tool-dragging");
        try {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", draggingToolId);
          e.dataTransfer.dropEffect = "move";
          if (dragSourceEl) {
            const rect = dragSourceEl.getBoundingClientRect();
            const ghost = dragSourceEl.cloneNode(true);
            ghost.classList.add("tool-drag-ghost");
            ghost.style.width = `${Math.round(rect.width)}px`;
            ghost.style.height = `${Math.round(rect.height)}px`;
            ghost.style.position = "fixed";
            ghost.style.left = "-10000px";
            ghost.style.top = "-10000px";
            ghost.style.pointerEvents = "none";
            document.body.appendChild(ghost);
            dragGhostEl = ghost;
            const offsetX = Math.max(8, Math.min(rect.width - 8, (e.clientX || rect.left) - rect.left));
            const offsetY = Math.max(8, Math.min(rect.height - 8, (e.clientY || rect.top) - rect.top));
            e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
          }
        } catch {}
        requestAnimationFrame(() => {
          if (!dragSourceEl || !draggingToolId) return;
          dragSourceEl.classList.add("tool-drag-source-collapsed");
        });
      });
      root.addEventListener("dragend", () => {
        suppressSummaryToggleUntil = Date.now() + 250;
        clearDragState();
      });
      root.addEventListener("dragover", (e) => {
        if (draggingCategory) {
          const dockRoot = e.target?.closest?.("[data-dock-root]");
          if (!dockRoot) return;
          const dropDock = String(dockRoot.dataset.dockRoot || "");
          if (!["left", "right"].includes(dropDock)) return;
          e.preventDefault();
          if (dropDock === draggingCategory.fromDock) {
            const categoryState = getCategoryPointerDropState(dockRoot, e.clientY);
            clearDropHints();
            categoryState.targetEl?.classList?.add(categoryState.after ? "tool-drag-over-after" : "tool-drag-over-before");
          } else {
            clearDropHints();
          }
          return;
        }
        if (!draggingToolId) return;
        const dockRoot = e.target?.closest?.("[data-dock-root]");
        const onEndZone = !!e.target?.closest?.('[data-tool-drop-end="1"]');
        if (!dockRoot) return;
        e.preventDefault();

        const pointerState = getPointerDropState(dockRoot, e.clientY, onEndZone);
        setDropVisual(dockRoot, pointerState.targetEl, pointerState.after);
      });
      root.addEventListener("drop", (e) => {
        if (draggingCategory) {
          const dockRoot = e.target?.closest?.("[data-dock-root]");
          if (!dockRoot) return;
          const dropDock = String(dockRoot.dataset.dockRoot || "");
          if (!["left", "right"].includes(dropDock)) return;
          e.preventDefault();
          if (dropDock === draggingCategory.fromDock) {
            const categoryState = getCategoryPointerDropState(dockRoot, e.clientY);
            applyCategoryReorder(draggingCategory.category, categoryState.targetCategory, categoryState.after);
          } else {
            applyCategoryDrop(dropDock);
          }
          return;
        }
        if (!draggingToolId) return;
        const dockRoot = e.target?.closest?.("[data-dock-root]");
        if (!dockRoot) return;
        const dropDock = dockRoot.dataset.dockRoot;
        if (!["left", "right", "bottom"].includes(dropDock)) return;
        e.preventDefault();
        const onEndZone = !!e.target?.closest?.('[data-tool-drop-end="1"]');
        const pointerState = getPointerDropState(dockRoot, e.clientY, onEndZone);
        const targetId = pointerState.targetId || (dropVisualState?.dock === dropDock ? dropVisualState.targetId : "");
        const after = pointerState.targetId ? pointerState.after : !!(dropVisualState?.dock === dropDock && dropVisualState.after);
        if (targetId && targetId !== draggingToolId) {
          applyDrop(dropDock, targetId, after);
          return;
        }
        if (dragPlaceholderEl?.parentElement && dragPlaceholderEl.closest?.("[data-dock-root]") === dockRoot) {
          const next = dragPlaceholderEl.nextElementSibling?.matches?.("details[data-tool-id]")
            ? dragPlaceholderEl.nextElementSibling
            : null;
          const prev = dragPlaceholderEl.previousElementSibling?.matches?.("details[data-tool-id]")
            ? dragPlaceholderEl.previousElementSibling
            : null;
          const nextId = next?.dataset?.toolId;
          const prevId = prev?.dataset?.toolId;
          if (nextId && nextId !== draggingToolId) {
            applyDrop(dropDock, nextId, false);
            return;
          }
          if (prevId && prevId !== draggingToolId) {
            applyDrop(dropDock, prevId, true);
            return;
          }
        }
        applyDrop(dropDock);
      });
      root.addEventListener("dragleave", () => {});
    });

    document.addEventListener("dragend", () => {
      suppressSummaryToggleUntil = Date.now() + 250;
    }, { once: true });

    for (const tool of sortedEnabled) {
      const panelEl = document.getElementById(`tool-panel-${tool.id}`);
      const def = allById.get(tool.id);
      if (!panelEl || !def?.renderPanel) continue;
      const preservedPanelEl = preservedToolPanels.get(tool.id);
      if (preservedPanelEl) {
        panelEl.replaceWith(preservedPanelEl);
        continue;
      }
      try {
        performance.mark(`tool:render:start:${tool.id}`);
        def.renderPanel(appContext, panelEl);
        performance.mark(`tool:render:end:${tool.id}`);
        performance.measure(`tool:render:${tool.id}`, `tool:render:start:${tool.id}`, `tool:render:end:${tool.id}`);
        if (!initializedPanelSubsets.has(tool.id) && tool.id !== "global-layers") {
          panelEl.querySelectorAll("details").forEach((d) => {
            d.open = false;
          });
          initializedPanelSubsets.add(tool.id);
        }
        restorePanelDetailOpenState(tool.id, panelEl);
      } catch (e) {
        console.error(`[Tool:${tool.id}] renderPanel failed`, e);
        panelEl.innerHTML = `<div class="small">Failed to render ${tool.name}</div>`;
      }
    }
    for (const tool of sortedEnabled) {
      setToolPanelOpen(tool.id, openPanels.has(`tool:${tool.id}`));
    }
    renderBuilderMenu();
  }

  normalizeToolOrder();
  normalizeCategoryOrder();
  normalizeDockAssignments();
  for (const tool of TOOL_DEFS) {
    if (!allowedToolIds.has(tool.id)) continue;
    if (!enabled.has(tool.id)) continue;
    const ok = enableTool(tool.id);
    if (!ok) enabled.delete(tool.id);
  }
  persistEnabled();
  renderSidebar();

  async function importDroppedFiles(fileList) {
    const files = [...(fileList || [])].filter(Boolean);
    if (!files.length) return { handled: false, imported: 0 };
    const supportedVector = files.filter((file) => /\.(geojson|json|kml|kmz|gpx|dxf|zip|shp|dbf|prj|cpg)$/i.test(String(file?.name || "")));
    if (!supportedVector.length) return { handled: false, imported: 0 };
    if (!enabled.has("linework")) {
      const ok = setToolEnabled("linework", true, { silent: true });
      if (!ok) return { handled: false, imported: 0 };
    }
    const linework = allById.get("linework");
    if (!linework?.handleExternalFiles) return { handled: false, imported: 0 };
    appContext.ui.toast(`Dispatching ${supportedVector.length} file${supportedVector.length === 1 ? "" : "s"} to Linework...`);
    const result = await linework.handleExternalFiles(appContext, supportedVector);
    const imported = Number(result?.imported || 0);
    return { handled: imported > 0, imported, toolId: "linework", pendingSiblingLookup: result?.pendingSiblingLookup === true };
  }

  return {
    listAllTools,
    isEnabled: (id) => enabled.has(id),
    setToolEnabled,
    renderSidebar,
    exportProjectData,
    importProjectData,
    importDroppedFiles,
    resolveProjectReference,
    destroy() {
      closeBuilderMenu();
      if (builderButtonEl?.parentElement) builderButtonEl.parentElement.removeChild(builderButtonEl);
      builderButtonEl = null;
      for (const id of [...active]) disableTool(id);
    },
  };
}
